export class FormFiltersElement extends HTMLElement {
  #DEBOUNCE_MS = 450;
  #SCROLL_KEY = 'filters_scroll_restore';

  constructor() {
    super();
    this._formElement = null;

    this._timer = null;
    this._lastSignature = null;

    this._scheduleSubmit = this._scheduleSubmit.bind(this);
    this._handleReset = this._handleReset.bind(this);
  }

  connectedCallback() {
    this.#restoreScrollOnce();

    const form = this.formElement;
    if (!form) return;

    this._lastSignature = this.#getFormSignature();

    form.addEventListener('input', (e) => {
      if (e.target?.type === 'range') return;
      this._scheduleSubmit();
    }, { passive: true });

    form.addEventListener(
      'pointerup',
      (e) => {
        if (e.target?.type !== 'range') return;

        this._scheduleSubmit();
      },
      { passive: true }
    );

    form.addEventListener('change', (e) => {
      if (e.target?.type === 'range') return;
      this._scheduleSubmit()
    }, { passive: true });

    form.addEventListener('keyup', (e) => {
      const target = e.target;

      if (target?.type !== 'range') return;

      const allowedKeys = [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'PageUp',
        'PageDown',
      ];
      if (!allowedKeys.includes(e.key)) return;

      this._scheduleSubmit();
    });

    form.addEventListener('reset', this._handleReset);
  }

  disconnectedCallback() {
    const form = this.formElement;
    if (!form) return;

    clearTimeout(this._timer);
    form.removeEventListener('input', this._scheduleSubmit);
    form.removeEventListener('change', this._scheduleSubmit);
    form.removeEventListener('reset', this._handleReset);
  }

  get formElement() {
    return (this._formElement ??= this.querySelector('.filters-form'));
  }

  _scheduleSubmit() {
    clearTimeout(this._timer);

    this._timer = setTimeout(() => {
      const signature = this.#getFormSignature();
      if (!signature || signature === this._lastSignature) return;

      this._lastSignature = signature;
      this.#submitForm();

    }, this.#DEBOUNCE_MS);
  }

  _handleReset() {
    clearTimeout(this._timer);

    setTimeout(() => {
      this._lastSignature = this.#getFormSignature();
      this.#submitForm();
    }, 0);
  }

  #submitForm() {
    const form = this.formElement;
    if (!form) return;

    this.#saveScrollForNextPage();

    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  #getFormSignature() {
    const form = this.formElement;
    if (!form) return null;

    const fd = new FormData(form);
    const entries = [];

    for (const [k, v] of fd.entries()) entries.push([k, String(v)]);
    entries.sort((a, b) => (a[0] + a[1]).localeCompare(b[0] + b[1]));

    return JSON.stringify(entries);
  }

  #saveScrollForNextPage() {
    const payload = {
      y: window.scrollY,
      path: window.location.pathname,
      ts: Date.now(),
    };

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    sessionStorage.setItem(this.#SCROLL_KEY, JSON.stringify(payload));
  }

  #restoreScrollOnce() {
    const raw = sessionStorage.getItem(this.#SCROLL_KEY);
    if (!raw) return;

    sessionStorage.removeItem(this.#SCROLL_KEY);

    let data;
    try { data = JSON.parse(raw); } catch { return; }

    if (!data?.ts || (Date.now() - data.ts) > 10_000) return;
    if (data.path && data.path !== window.location.pathname) return;

    const y = Number(data.y);
    if (!Number.isFinite(y)) return;

    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    }));
  }
}